import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Github,
  Gamepad as GameIcon,
  Mail,
  Lock,
  User,
  Loader2,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "../../components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Separator } from "../../components/ui/separator";

import { API_BASE_URL, TURNSTILE_SITE_KEY } from '../../config';
import { brand } from '../../config/brand';

export default function Login(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Read error from query params (for Google OAuth failures)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      setError(errorParam);
    }
  }, [location]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  // Turnstile captcha state (only for registration)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Render/destroy Turnstile when switching to/from register mode
  useEffect(() => {
    // Cleanup previous widget
    if (widgetIdRef.current && (window as any).turnstile) {
      try {
        (window as any).turnstile.remove(widgetIdRef.current);
      } catch (e) { }
      widgetIdRef.current = null;
    }
    setTurnstileToken(null);

    if (!isRegister) return;

    let isMounted = true;

    const renderTurnstile = () => {
      if (!isMounted || !turnstileRef.current || widgetIdRef.current) return;
      if ((window as any).turnstile) {
        try {
          const wid = (window as any).turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'dark',
            callback: (token: string) => {
              if (isMounted) setTurnstileToken(token);
            },
            'expired-callback': () => {
              if (isMounted) setTurnstileToken(null);
            },
            'error-callback': () => {
              if (isMounted) setTurnstileToken(null);
            },
          });
          widgetIdRef.current = wid;
        } catch (e) {
          console.warn('Turnstile render failed:', e);
        }
      }
    };

    if ((window as any).turnstile) {
      // Small delay to ensure the ref is mounted in DOM
      setTimeout(renderTurnstile, 100);
    } else {
      const interval = setInterval(() => {
        if ((window as any).turnstile) {
          renderTurnstile();
          clearInterval(interval);
        }
      }, 500);
      return () => {
        isMounted = false;
        clearInterval(interval);
      };
    }

    return () => {
      isMounted = false;
      if (widgetIdRef.current && (window as any).turnstile) {
        try {
          (window as any).turnstile.remove(widgetIdRef.current);
        } catch (e) { }
        widgetIdRef.current = null;
      }
    };
  }, [isRegister]);

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const strength = getPasswordStrength(formData.password);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Require captcha on registration
    if (isRegister && !turnstileToken) {
      setError('Please complete the captcha verification.');
      setLoading(false);
      return;
    }

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      const payload = isRegister ? { ...formData, turnstileToken } : formData;
      const response = await axios.post(`${API_BASE_URL}${endpoint}`, payload);

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      // Redirect to dashboard or back to pricing if there was an intent
      const intent = localStorage.getItem('checkout_intent');
      if (intent) {
        history.push('/pricing');
      } else {
        history.push('/dashboard');
      }
      window.location.reload(); // Refresh to update global state/navbar
    } catch (err: any) {
      setError(err.response?.data?.error || 'An error occurred during authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = `${API_BASE_URL}/auth/google`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md bg-card border-border shadow-2xl">
        <CardHeader className="space-y-1 flex flex-col items-center">
          <div className="bg-primary p-3 rounded-2xl mb-4 shadow-lg shadow-primary/20">
            <img src={brand.logo} alt={brand.name} className="h-10 w-auto brightness-0 invert" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            {isRegister ? 'Create Account' : 'Welcome Back'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {brand.name} Dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="bg-red-500/10 border-red-500/20 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-foreground">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    placeholder="John Doe"
                    className="pl-10 bg-background border-border text-foreground focus:ring-primary"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-foreground">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="name@example.com"
                  required
                  className="pl-10 bg-background border-border text-foreground focus:ring-primary"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="pl-10 bg-background border-border text-foreground focus:ring-primary"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>

              {!isRegister && (
                <div className="flex justify-end px-1">
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-[11px] font-bold text-muted-foreground hover:text-primary uppercase tracking-wider"
                    onClick={() => history.push('/forgot-password')}
                  >
                    Forgot Password?
                  </Button>
                </div>
              )}

              {isRegister && formData.password.length > 0 && (
                <div className="space-y-1.5 mt-2">
                  <div className="flex justify-between items-center px-1">
                    <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Security level</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${strength <= 1 ? 'text-red-400' :
                        strength === 2 ? 'text-orange-400' :
                          strength === 3 ? 'text-yellow-400' :
                            'text-green-400'
                      }`}>
                      {strength <= 1 ? 'Weak' :
                        strength === 2 ? 'Fair' :
                          strength === 3 ? 'Good' :
                            'Strong'}
                    </span>
                  </div>
                  <div className="flex gap-1 h-1.5">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`flex-1 rounded-full transition-all duration-500 ${level <= strength ? (
                            strength <= 1 ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]' :
                              strength === 2 ? 'bg-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.4)]' :
                                strength === 3 ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.4)]' :
                                  'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]'
                          ) : 'bg-card'
                          }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {isRegister && (
              <div className="flex justify-center py-2">
                <div ref={turnstileRef}></div>
              </div>
            )}

            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold py-6 mt-2 transition-all active:scale-[0.98]"
              disabled={loading || (isRegister && !turnstileToken)}
              type="submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Please wait...
                </>
              ) : (
                isRegister ? 'Sign Up' : 'Sign In'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <Separator className="bg-muted" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">
                Or continue with
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full border-border text-foreground hover:bg-muted py-6"
            onClick={handleGoogleLogin}
          >
            <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="google" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512">
              <path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path>
            </svg>
            Google
          </Button>
        </CardContent>
        <CardFooter className="flex flex-col space-y-2">
          <Button
            variant="link"
            className="text-primary hover:text-primary/80"
            onClick={() => setIsRegister(!isRegister)}
          >
            {isRegister ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

